import API_URL from "../apiConfig";
import {
  clearRuntimeSession,
  getAccessToken,
  getSessionInfo,
  registerRefreshHandler,
  setAccessToken,
  setSessionInfo,
} from "./sessionRuntime";

const SESSION_KEY = "zedro_session";

function parseJson(text) {
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

function readCachedSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function readLegacyToken() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return String(parsed?.token || "");
  } catch {
    return "";
  }
}

function writeCachedSession({ user = null, session = null } = {}) {
  if (!user) {
    localStorage.removeItem(SESSION_KEY);
    return;
  }

  // Nunca persistimos access token ni refresh token en localStorage.
  localStorage.setItem(
    SESSION_KEY,
    JSON.stringify({ user, session: session || null })
  );
}

function applyAuthenticationPayload(data) {
  const token = data?.access_token || data?.token || "";
  const user = data?.user || readCachedSession()?.user || null;
  const session = data?.session || getSessionInfo() || null;

  setAccessToken(token);
  setSessionInfo(session);
  writeCachedSession({ user, session });

  return { token, user, session };
}

export function getCurrentSession() {
  const cached = readCachedSession();

  return {
    token: getAccessToken(),
    user: cached?.user || null,
    session: getSessionInfo() || cached?.session || null,
  };
}

export function setSession(session) {
  if (!session) {
    clearLocalSession();
    return;
  }

  applyAuthenticationPayload(session);
}

export function getCurrentUser() {
  return readCachedSession()?.user || null;
}

export function clearLocalSession() {
  clearRuntimeSession();
  localStorage.removeItem(SESSION_KEY);
}

export async function refreshSessionToken() {
  const res = await fetch(`${API_URL}/api/session/refresh`, {
    method: "POST",
    headers: { Accept: "application/json" },
    credentials: "include",
  });

  const data = parseJson(await res.text().catch(() => ""));

  if (!res.ok || !data?.access_token) {
    clearLocalSession();
    return "";
  }

  return applyAuthenticationPayload(data).token;
}

registerRefreshHandler(refreshSessionToken);

export async function restoreSession() {
  const legacyToken = readLegacyToken();
  const token = await refreshSessionToken();

  if (token) return getCurrentSession();

  if (!legacyToken) return null;

  const res = await fetch(`${API_URL}/api/session/migrate`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${legacyToken}`,
    },
    credentials: "include",
  });

  const data = parseJson(await res.text().catch(() => ""));
  if (!res.ok || !data?.access_token) {
    clearLocalSession();
    return null;
  }

  applyAuthenticationPayload(data);
  return getCurrentSession();
}

export async function validateActivationToken(token) {
  const res = await fetch(`${API_URL}/api/activate/${token}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });

  const data = parseJson(await res.text().catch(() => ""));

  if (!res.ok) {
    throw new Error(
      data?.message || data?.error || "El enlace de activación no es válido."
    );
  }

  return data;
}

export async function activateAccount(token, password) {
  const res = await fetch(`${API_URL}/api/activate/${token}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ password }),
  });

  const data = parseJson(await res.text().catch(() => ""));

  if (!res.ok) {
    throw new Error(data?.message || data?.error || "No se pudo activar la cuenta");
  }

  applyAuthenticationPayload(data);
  return data;
}

export async function me() {
  if (!getAccessToken()) {
    return { ok: false, user: null };
  }

  const res = await fetch(`${API_URL}/api/me`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
    credentials: "include",
  });

  if (!res.ok) {
    const data = parseJson(await res.text().catch(() => ""));
    const error = new Error(data?.error || "No se pudo rehidratar /api/me");
    error.status = res.status;
    error.data = data;
    throw error;
  }

  const data = await res.json();
  const cached = getCurrentSession();
  writeCachedSession({ user: data.user || null, session: cached.session });

  return {
    ok: true,
    user: data.user || null,
    subscriptions: data.subscriptions || [],
    products: data.products || [],
    core_progress: data.core_progress || [],
  };
}

export async function login(email, password) {
  const res = await fetch(`${API_URL}/api/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ email, password }),
  });

  const data = parseJson(await res.text().catch(() => ""));

  if (!res.ok) {
    const error = new Error(
      data?.message || data?.error || `Login falló (${res.status})`
    );
    error.status = res.status;
    error.data = data;
    throw error;
  }

  applyAuthenticationPayload(data);
  return data;
}

export async function updateProfile(payload) {
  const res = await fetch(`${API_URL}/api/me/profile`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  const data = parseJson(await res.text().catch(() => ""));

  if (!res.ok) {
    throw new Error(data?.error || "No se pudo actualizar el perfil");
  }

  const current = getCurrentSession();
  writeCachedSession({
    user: data?.user || current.user,
    session: current.session,
  });

  return data;
}

export async function requestPasswordReset(email) {
  const res = await fetch(`${API_URL}/api/password/forgot`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ email }),
  });

  const data = parseJson(await res.text().catch(() => ""));
  if (!res.ok) {
    throw new Error(
      data?.message ||
        data?.error ||
        `No se pudo solicitar el restablecimiento (${res.status})`
    );
  }
  return data;
}

export async function resetPassword({ token, password }) {
  const res = await fetch(`${API_URL}/api/password/reset`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ token, password }),
  });

  const data = parseJson(await res.text().catch(() => ""));
  if (!res.ok) {
    throw new Error(
      data?.message ||
        data?.error ||
        `No se pudo restablecer la contraseña (${res.status})`
    );
  }

  clearLocalSession();
  return data;
}

export async function logout() {
  try {
    await fetch(`${API_URL}/api/session/logout`, {
      method: "POST",
      headers: { Accept: "application/json" },
      credentials: "include",
    });
  } finally {
    clearLocalSession();
    try {
      localStorage.setItem("zedro::logout", Date.now().toString());
    } catch {}
  }
}

export async function logoutAllSessions() {
  const res = await fetch(`${API_URL}/api/session/logout-all`, {
    method: "POST",
    headers: { Accept: "application/json" },
    credentials: "include",
  });

  const data = parseJson(await res.text().catch(() => ""));

  if (!res.ok) {
    throw new Error(
      data?.message || data?.error || "No se pudieron cerrar las sesiones."
    );
  }

  clearLocalSession();
  try {
    localStorage.setItem("zedro::logout", Date.now().toString());
  } catch {}

  return data;
}

export async function reauthenticate(password) {
  const res = await fetch(`${API_URL}/api/session/reauth`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify({ password }),
  });

  const data = parseJson(await res.text().catch(() => ""));

  if (!res.ok) {
    const error = new Error(
      data?.message || data?.error || "No se pudo confirmar tu identidad."
    );
    error.status = res.status;
    error.data = data;
    throw error;
  }

  if (data?.access_token || data?.token) {
    applyAuthenticationPayload(data);
  }

  return data;
}

export async function touchSession() {
  if (!getAccessToken()) return { ok: false };

  const res = await fetch(`${API_URL}/api/session/touch`, {
    method: "POST",
    headers: { Accept: "application/json" },
    credentials: "include",
  });

  if (!res.ok) return { ok: false };
  return res.json();
}

export async function cancelMembership() {
  const res = await fetch(`${API_URL}/api/member/billing/cancel-membership`, {
    method: "POST",
    headers: { Accept: "application/json" },
    credentials: "include",
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || "No se pudo cancelar la suscripción.");
  }
  return data;
}

export async function reactivateMembership() {
  const res = await fetch(`${API_URL}/api/member/billing/reactivate-membership`, {
    method: "POST",
    headers: { Accept: "application/json" },
    credentials: "include",
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || "No se pudo reactivar la membresía.");
  }
  return data;
}
